import { Box, Grid, Tabs, Tab, Typography, Badge, useTheme, createTheme, ThemeProvider } from "@mui/material";
import PropTypes from 'prop-types';
import React, { useState, useEffect } from "react";
import { tokens } from "../../../theme"
import { useParams, useLocation, useNavigate } from "react-router-dom"
import Header from "../global/Header";
import PulseLoader from "react-spinners/PulseLoader";
import useAuth from "../../../hooks/useAuth";
import CampaignBuilder from "../campaign_builder/CampaignBuilder";
import { useGetUsersQuery } from "../../../features/users/usersApiSlice";
import { useMemo } from "react";
import AgencyLdc from "../agency_ldc";
import AdcMetrics from "../adc_metrics";
import TeamManagement from "../team_management";
import DeliveryConfig from "./adc_config";
import Intent from "./intent";

import OrganizationDashboard from "../organization_dashboard";
import { useDiscreteMode } from "../global/DiscreteMode";
import { useGetOrganizationByOrganizationIdQuery } from "../../../features/organizations/organizationsApiSlice";
import ApiDashboard from "../apis_dashboard";


function TabPanel(props) {
  const { children, value, index, ...other } = props;



  return (
    <span
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </span>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}




const Organization = () => {
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const tabTheme = createTheme({
    typography: {
      fontFamily: [
        'Source Sans Pro',
        'sans-serif'
      ].join(','),
    },
    components: {
      MuiTab: {
        styleOverrides: {
          root: {
            color: colors.grey[200],  // Non-selected tabs
            '&.Mui-selected': {
              color: colors.greenAccent[500],  // Selected tab
            },
            '&:hover': {
              color: colors.greenAccent[300],  // Slightly lighter on hover, adjust as needed
              opacity: 0.9,
            },
          },
        },
      },
    },
  });

  const params = useParams();

  const organization_id = params.organization_id;

  const organization = useGetOrganizationByOrganizationIdQuery(params.organization_id).data;

  /* pick up ?tab=ADC from URL */
  const location = useLocation();
  const navigate = useNavigate();
  const queryTab = new URLSearchParams(location.search).get('tab');


  const tabs = [
    { label: "Overview", alwaysShow: true },
    { label: "Delivery Config", alwaysShow: true },
    { label: "Users", alwaysShow: true },
    {
      label: "ADC",
      condition: () =>
        organization?.adc_enabled ||
        (Array.isArray(organization?.adc_counts) && organization.adc_counts.length > 0),
    },
    {
      label: "APIs",
      condition: () =>
        organization?.api_config?.enabled ||
        (Array.isArray(organization?.products) && organization.products.includes("APIs")),
    },
    { label: "LDC", condition: () => organization?.ldc_enabled },
    { label: "Intent", alwaysShow: true },
    {
      label: "Campaign Builder",
      condition: () =>
        Array.isArray(organization?.products) &&
        organization.products.includes("Campaign Builder"),
    },
  ];

  const visibleTabs = tabs.filter(tab => tab.alwaysShow || (tab.condition && tab.condition()));
  const tabIndices = useMemo(() => visibleTabs.reduce((acc, tab, index) => ({ ...acc, [tab.label]: index }), {}), [visibleTabs]);

  /* whenever tab list is ready & URL says ADC → jump to that tab */
  useEffect(() => {
    if (queryTab && tabIndices[queryTab] !== undefined) {
      setValue(tabIndices[queryTab]);        // jump once

      // strip ?tab=… so the effect won’t trigger again
      navigate(location.pathname, { replace: true });
    }
  }, [queryTab, tabIndices, navigate, location.pathname]);


  const { isDiscreteMode } = useDiscreteMode();


  const {
    data: users,
    isLoading,
    isSuccess,
    isError,
    error
  } = useGetUsersQuery('usersList', {
    pollingInterval: 60000,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  })




  let content

  if (isLoading) content = <PulseLoader color={"#FFF"} />


  const TabManager = ({ organization, value, handleChange }) => {
    const tabs = [
      { label: "Overview", alwaysShow: true },
      { label: "Delivery Config", alwaysShow: true },
      { label: "Users", alwaysShow: true },
      {
        label: "ADC",
        condition: () =>
          organization?.adc_enabled ||
          (Array.isArray(organization?.adc_counts) && organization.adc_counts.length > 0),
      },
      {
        label: "APIs",
        condition: () =>
          organization?.api_config?.enabled ||
          (Array.isArray(organization?.products) && organization.products.includes("APIs")),
      },
      { label: "LDC", condition: () => organization?.ldc_enabled },
      { label: "Intent", alwaysShow: true },
      {
        label: "Campaign Builder",
        condition: () =>
          Array.isArray(organization?.products) &&
          organization.products.includes("Campaign Builder"),
      },
    ];

    const visibleTabs = tabs.filter(tab => tab.alwaysShow || (tab.condition && tab.condition()));

    return (
      <ThemeProvider theme={tabTheme}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            {visibleTabs.map((tab, index) => (
              <Tab key={tab.label} label={tab.label} {...a11yProps(index)} />
            ))}
          </Tabs>
        </Box>
      </ThemeProvider>
    );
  };


  const [partnerIdFilter, setPartnerIdFilter] = React.useState('');
  const [clientNameFilter, setClientNameFilter] = React.useState('');
  const [selectedCells, setSelectedCells] = React.useState([]);
  const hasS3Config = !!organization?.s3_config?.infra_data?.onprem_config?.bucket_created;


  const { username, isManager, isAdmin } = useAuth();

  // Add state for the switch
  const [viewBotFilteredResults, setViewBotFilteredResults] = useState(false);

  // Function to handle the switch state change
  const handleToggleSwitch = (event) => {
    setViewBotFilteredResults(event.target.checked);
  };



  if (isError) {
    console.log("Error retrieving users")
    content = <p className="errmsg">{error?.data?.message}</p>
  }

  if (isSuccess && organization) {
    const userList = Object.values(users.entities);

    let lifetimeSummaryTitle = '';
    let summaryPercentResolved = '';
    let summaryUniquePercentResolved = '';
    let adcRows = [];

    if (organization.adc_enabled && organization.adc_counts) {
      var summaryObject = organization.adc_counts.find((item) => item.on_date === 'summary');

      if (summaryObject) {
        lifetimeSummaryTitle = `Lifetime Summary: Updated at - ${summaryObject.count_time}`;
        summaryPercentResolved = ((summaryObject.visits_resolved_count / summaryObject.visits_count) * 100).toFixed(2);
        summaryUniquePercentResolved = ((summaryObject.distinct_visits_resolved_count / summaryObject.distinct_visits_count) * 100).toFixed(2);
      }



      if (viewBotFilteredResults) {
        adcRows = organization.adc_counts
          .filter((item) => item.on_date !== 'summary' && item.visits_count_bot_filtered !== undefined)
          .map((item) => {
            const percentResolved = item.resolved_visits_count_bot_resolved / item.visits_count_bot_filtered;
            const uniquePercentResolved = item.distinct_visits_resolved_count_bot_resolved / item.distinct_visits_count_bot_filtered;

            const originalPercentResolved = item.visits_resolved_count / item.visits_count;
            const originalUniquePercentResolved = item.distinct_visits_resolved_count / item.distinct_visits_count;

            const deltaVisitsCount = item.visits_count - item.visits_count_bot_filtered;
            const deltaDistinctVisitsCount = item.distinct_visits_count - item.distinct_visits_count_bot_filtered;
            const deltaResolvedCount = item.visits_resolved_count - item.visits_resolved_count_bot_filtered;
            const deltaDistinctResolvedCount = item.distinct_visits_resolved_count - item.distinct_visits_resolved_count_bot_filtered;

            const deltaDistinctHemsCount = item.distinct_hems_count - item.distinct_hems_count_bot_filtered;

            const deltaPercentResolved = -1 * (originalPercentResolved - percentResolved);
            const deltaUniquePercentResolved = -1 * (originalUniquePercentResolved - uniquePercentResolved);

            return {
              ...item,
              visits_count: item.visits_count,
              visits_resolved_count: item.visits_resolved_count,
              distinct_visits_count: item.distinct_visits_count,
              distinct_visits_resolved_count: item.distinct_visits_resolved_count,
              percent_resolved: percentResolved,
              unique_percent_resolved: uniquePercentResolved,
              delta_visits_count: deltaVisitsCount,
              delta_distinct_visits_count: deltaDistinctVisitsCount,
              distinct_hems_count: item.distinct_hems_count,
              delta_distinct_hems_count: deltaDistinctHemsCount,
              delta_resolved_count: deltaResolvedCount,
              delta_distinct_resolved_count: deltaDistinctResolvedCount,
              delta_percent_resolved: deltaPercentResolved,
              delta_unique_percent_resolved: deltaUniquePercentResolved
            };
          });
      } else {
        adcRows = organization.adc_counts
          .filter((item) => item.on_date !== 'summary')
          .map((item) => {
            const percentResolved = item.visits_resolved_count / item.visits_count;
            const uniquePercentResolved = item.distinct_visits_resolved_count / item.distinct_visits_count;
            return {
              ...item,
              percent_resolved: percentResolved,
              unique_percent_resolved: uniquePercentResolved,
            };
          });
      }
    }

    const { ids, entities } = users;

    function filterById(obj) {
      return obj.organization_id === organization_id;
    }

    const filtered_users = userList.filter(filterById);

    content = (
      <Box m="20px">
        <Box display="flex" justifyContent="end" mt="20px">
        </Box>
        <Grid container alignItems="center" spacing={16}>
          <Grid item>
            <Header
              title={
                <Box display="flex" alignItems="center">
                  {isDiscreteMode ? (
                    <Box width="150px" height="20px" bgcolor="white" />
                  ) : (
                    organization.name
                  )}
                </Box>
              }
              subtitle="Manage organization"
            />
          </Grid>
          {hasS3Config && (
            <Grid item >
              <Badge
                badgeContent="Delivery Ready"
                color="success"
                sx={{
                  '.MuiBadge-badge': {
                    backgroundColor: theme.palette.success.main,
                    color: 'white',
                    fontSize: '1.2rem', // Slightly larger font size
                    padding: '15px 25px', // Increased padding for a bigger badge
                    margin: theme.spacing(1.5), // Adjusted margin for more space
                    borderRadius: '25px', // Slightly more rounded corners
                    whiteSpace: 'nowrap', // Prevents text wrapping
                  }
                }}
              />
            </Grid>
          )}
          {!hasS3Config && (
            <Grid item marginLeft={7}>
              <Badge
                badgeContent="Delivery Not Ready"
                color="success"
                sx={{
                  '.MuiBadge-badge': {
                    backgroundColor: colors.redAccent[500],
                    color: 'white',
                    fontSize: '1.2rem', // Slightly larger font size
                    padding: '15px 25px', // Increased padding for a bigger badge
                    margin: theme.spacing(1.5), // Adjusted margin for more space
                    borderRadius: '25px', // Slightly more rounded corners
                    whiteSpace: 'nowrap', // Prevents text wrapping
                  }
                }}
              />
            </Grid>
          )}
        </Grid>
        <TabManager organization={organization} value={value} handleChange={handleChange} />
        <TabPanel value={value} index={tabIndices['Overview']}>
          <OrganizationDashboard organization={organization} adcRows={adcRows} />
        </TabPanel>
        <TabPanel value={value} index={tabIndices['Delivery Config']}>
          <DeliveryConfig value={value} index={1} organization={organization} />
        </TabPanel>
        {/*<TabPanel value={value} index={1}>
          <OrganizationDetails organization={organization} colors={colors} />
        </TabPanel>*/}
        <TabPanel value={value} index={tabIndices['Users']}>
          <TeamManagement organization_id={organization_id} filtered_users={filtered_users} colors={colors} />
        </TabPanel>
        {/**/}
        {(organization.adc_enabled || (organization.adc_counts?.length > 0)) &&
          <TabPanel value={value} index={tabIndices['ADC']}>
            <AdcMetrics
              organization={organization}
              isAdmin={isAdmin}
              viewBotFilteredResults={viewBotFilteredResults}
              handleToggleSwitch={handleToggleSwitch}
              summaryObject={summaryObject}
              lifetimeSummaryTitle={lifetimeSummaryTitle}
              summaryPercentResolved={summaryPercentResolved}
              summaryUniquePercentResolved={summaryUniquePercentResolved}
              adcRows={adcRows}
              colors={colors} // if it's not a default prop in your project, you should pass it down.
            />
          </TabPanel>
        }
        {(organization?.api_config?.enabled ||
          (Array.isArray(organization?.products) && organization.products.includes("APIs"))) && (
            <TabPanel value={value} index={tabIndices['APIs']}>
              <ApiDashboard organization={organization} />
            </TabPanel>
          )}
        {/* Conditionally render LDC panel */}
        {(organization.ldc_enabled && !organization.adc_enabled) &&
          <TabPanel value={value} index={tabIndices['LDC']}>
            <AgencyLdc organization={organization}
              partnerIdFilter={partnerIdFilter}
              setPartnerIdFilter={setPartnerIdFilter}
              clientNameFilter={clientNameFilter}
              setClientNameFilter={setClientNameFilter} />
          </TabPanel>
        }
        {(organization.ldc_enabled && organization.adc_enabled) &&
          <TabPanel value={value} index={tabIndices['Intent']}>
            <AgencyLdc organization={organization}
              partnerIdFilter={partnerIdFilter}
              setPartnerIdFilter={setPartnerIdFilter}
              clientNameFilter={clientNameFilter}
              setClientNameFilter={setClientNameFilter}
            />
          </TabPanel>
        }
        <TabPanel value={value} index={tabIndices['Intent']}>
          <Intent organization={organization} />
        </TabPanel>
        {(Array.isArray(organization?.products) &&
          organization.products.includes("Campaign Builder")) && (
            <TabPanel value={value} index={tabIndices['Campaign Builder']}>
              <CampaignBuilder organization={organization} onComplete={() => { /* no-op */ }} />
            </TabPanel>
          )}
      </Box>

    );
  }
  return content
}

export default Organization;